import { RECEIPT_CHANNELS, RECEIPT_CHANNEL_LABELS, type ReceiptInput } from '@chukua/common';
import { Alert, Button, DatePicker, Drawer, Form, Input, Select } from 'antd';
import dayjs from 'dayjs';
import { CompanyAccountSelect } from '../../../../company-accounts/public/CompanyAccountSelect';
import { CustomerSearchSelect } from '../../../../customers/public/CustomerSearchSelect';
import { toReceiptInput, type ReceiptFormValues } from '../policies/receipt-form';
import { ReceiptAmountField } from './ReceiptAmountField';
import { ReceiptReferenceField } from './ReceiptReferenceField';

const DATE_FORMAT = 'YYYY-MM-DD';
const CHANNEL_OPTIONS = RECEIPT_CHANNELS.map((channel) => ({ value: channel, label: RECEIPT_CHANNEL_LABELS[channel] }));

interface RecordReceiptDrawerProps {
  open: boolean;
  error: string | null;
  submitting: boolean;
  onRecord: (input: ReceiptInput) => Promise<boolean>;
  onClose: () => void;
}

/** Channel first, since it decides what the reference is. A refusal from the server shows at the top and the values stay. */
export function RecordReceiptDrawer({ open, error, submitting, onRecord, onClose }: RecordReceiptDrawerProps) {
  const [form] = Form.useForm<ReceiptFormValues>();
  const channel = Form.useWatch('channel', form);

  async function submit(values: ReceiptFormValues) {
    const recorded = await onRecord(toReceiptInput(values));
    if (recorded) {
      form.resetFields();
      onClose();
    }
  }

  return (
    <Drawer open={open} onClose={onClose} title="Record receipt" placement="right" size={480} destroyOnClose>
      {error && <Alert className="mb-5" type="error" title={error} showIcon />}
      <Form<ReceiptFormValues> form={form} layout="vertical" requiredMark={false} onFinish={submit} disabled={submitting} initialValues={{ currency: 'KES' }}>
        <Form.Item name="channel" label="Channel" rules={[{ required: true, message: 'Choose how it was paid' }]}>
          {/* The account list depends on the channel, so a previous choice may no longer be one of the options. */}
          <Select options={CHANNEL_OPTIONS} placeholder="M-Pesa, cheque or bank transfer" onChange={() => form.resetFields(['companyAccountId'])} />
        </Form.Item>
        <ReceiptReferenceField />
        <Form.Item name="customerId" label="Customer" rules={[{ required: true, message: 'Choose a customer' }]}>
          <CustomerSearchSelect />
        </Form.Item>
        <ReceiptAmountField />
        <Form.Item
          name="receivedOn"
          label="Date received"
          rules={[{ required: true, message: 'Enter the date received' }]}
          getValueProps={(value?: string) => ({ value: value ? dayjs(value) : undefined })}
          normalize={(day?: dayjs.Dayjs) => day?.format(DATE_FORMAT)}
        >
          <DatePicker className="w-full" disabledDate={(day) => day.isAfter(dayjs(), 'day')} />
        </Form.Item>
        <Form.Item name="companyAccountId" label="Account" rules={[{ required: true, message: 'Choose the account it was paid into' }]}>
          <CompanyAccountSelect channel={channel} />
        </Form.Item>
        <Form.Item name="payer" label="Payer" rules={[{ required: true, whitespace: true, message: 'Enter who paid' }]}>
          <Input />
        </Form.Item>
        <Form.Item className="mb-0 pt-2">
          <Button type="primary" htmlType="submit" loading={submitting}>
            Record receipt
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
}
